import { AxiosResponse } from "axios";
import businessPortalClient from "../businessPortalClient";
import { DataTableContextFilters } from "../components/DataTable/DataTableContext/DataTableContextProvider";
import { Statuses } from "../components/DelegeeStatus/DelegeeStatus";
import { DelegatedPedestrianRightsWithPagination, PedestrianDelegeesWithPagination } from "../models/delegations/DelegatedPedestrianRight";
import { WithFiltersAndPaging } from "../models/filters/WithFiltersAndPaging";
import { ForFleetManager } from "../models/ForFleetManager";
import { InvitePedestrianRightRequestModel } from "../models/InvitePedestrianRightRequestModel";
import { PedestrianProductModel } from "../models/PedestrianProductModel";
import { DeletePedestrianDelegeesModel } from "../models/vehicle-assignments/DeletePedestrianDelegeesModel";
import { RevokePedestrianRightsModel } from "../models/vehicle-assignments/RevokePedestrianRightsModel";
import queryParam from "../utils/queryParam.utils";
import { getQueryString } from "../utils/url.utils";

const baseUrl = "/api/pedestrianrights";

export type GetDelegatedPedestrianRightsQuery = {
  seasonTicketOwnerCrmId: string;
  placeId: number;
  language: string;
  pmcIds?: number[];
  name?: string;
  email?: string;
  statuses?: Statuses[];
  page: number;
  pageSize: number;
  sortBy?: 'name' | 'email' | 'invitationDate' | 'status';
  ascending?: boolean;
  invitationFrom?: string;
  invitationTo?: string;
};

export function getDelegatedPedestrianRights(q: GetDelegatedPedestrianRightsQuery) : Promise<AxiosResponse<DelegatedPedestrianRightsWithPagination>> {
  const { seasonTicketOwnerCrmId, language, ...others } = q;
  const qs = getQueryString(others);

  const url = `${baseUrl}/GetDelegatedPedestrianRights/${seasonTicketOwnerCrmId}/${language}?${qs}`
  return businessPortalClient.get(url);
}

export function getPedestrianProducts(
  seasonTicketOwnerCrmId: string,
  language: string
) : Promise<AxiosResponse<PedestrianProductModel[]>> {
  const url = `${baseUrl}/getpedestrianproducts/${seasonTicketOwnerCrmId}/${language}`
  return businessPortalClient.get(url);
}

export type AssignedPedestrianProductInfo = {
  pmcId: number;
  entryRightId: string;
  contractId: string;
};

export type InvitePedestrianRightResponse = {
  delegeeId: string;
  assignedPedestrianProducts: AssignedPedestrianProductInfo[]
};

export function inviteDelegee(request: InvitePedestrianRightRequestModel) : Promise<AxiosResponse<InvitePedestrianRightResponse>> {
  const url = `${baseUrl}/invitedelegee`
  return businessPortalClient.post(url, request);
}

export function revokePedestrianRights(model: RevokePedestrianRightsModel): Promise<AxiosResponse> {

  type ExpectedBody = {
    customerId: string;
    delegeeRevokeModels: { delegeeId: string; entryRightIds: string[] }[]
  };

  const body : ExpectedBody = {
    customerId: model.customerId,
    delegeeRevokeModels: model.delegeeRevokeModels.map(delegee => ({
      delegeeId: delegee.delegeeId,
      entryRightIds: delegee.products.map(product => product.entryRightId)
    }))
  }

  const url = `${baseUrl}/RevokePedestrianRights`;
  return businessPortalClient.post(url, body);
};

export type ResendInviteModel = {
  email: string;
  customerId: string;
}

export function resendInvite(model: ResendInviteModel) : Promise<AxiosResponse> {
  const url = `${baseUrl}/ResendInvite`;
  return businessPortalClient.post(url, model);
}

export function deletePedestrianDelegees(model: DeletePedestrianDelegeesModel): Promise<AxiosResponse> {
    const url = `${baseUrl}/DeletePedestrianDelegees`;
    return businessPortalClient.post(url, model);
};

export function getPedestrianDelegeesMyFleet(request: WithFiltersAndPaging<ForFleetManager>): Promise<AxiosResponse<PedestrianDelegeesWithPagination>> {
  const url = `${baseUrl}/getPedestrianDelegeesMyFleet/${request.entity.seasonTicketOwnerCrmId}/${request.entity.language}`;
  const params = queryParam.buildQueryParam(
    request.filters as DataTableContextFilters[],
    request.pagination
  );

  return businessPortalClient.get(url, { params });
};

export type PedestrianDelegeeDetails = {
  id : string;
  delegeeName: string;
  delegeeEmail: string;
  pmcIds: number[];
  status: Statuses;
}

export function getDelegeeDetails(id: string): Promise<AxiosResponse<PedestrianDelegeeDetails>> {
  const url = `${baseUrl}/GetDelegee/${id}`;
  return businessPortalClient.get(url);
}

export type UpdatePedestrianDelegeePayload  = {
  model: UpdatePedestrianRightDelegeeModel,
  delegeePmcIds: number[]
}

export type UpdatePedestrianRightDelegeeModel = {
  customerId: string;
  delegeeId: string;
  name: string;
  pmcIds: number[];
}

export type UpdatePedestrianRightResponse = {
  delegeeId: string;
  assignedPedestrianProducts: AssignedPedestrianProductInfo[]
};

export function updateDelegee(model: UpdatePedestrianRightDelegeeModel) : Promise<AxiosResponse<UpdatePedestrianRightResponse>> {
  const url = `${baseUrl}/UpdateDelegee`;
  return businessPortalClient.post(url, model);
}
